import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import React, { PropsWithChildren, useState } from 'react';
import Button from '@atlaskit/button/button';
import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box } from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import { useIntl } from 'react-intl';
import { TourSpotlighTargetName } from './TourSpotlightTarget';
import { TourSpotlightProvider } from './TourSpotlightContext';
import { messages } from './messages';

import type { Placement } from '@atlaskit/spotlight/types';
import { SpotlightSecondaryAction } from '@atlaskit/spotlight/secondary-action';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { PopoverContent, type PopoverContentProps } from '@atlaskit/spotlight/popover-content';

const backdropStyles = xcss({
	position: 'fixed',
	inset: 'space.0',
	// color.blanket is the ADS-standard semi-transparent overlay token,
	// designed to work correctly in both light and dark mode.
	backgroundColor: 'color.blanket',
	pointerEvents: 'none',
	zIndex: 'blanket',
});

const ModalHeaderStyles = xcss({
	display: 'flex',
	flexGrow: 1,
	justifyContent: 'flex-end',
});

const modalTextStyles = xcss({
	font: 'font.body',
});

export type TourItem =
	| {
			type: 'spotlight';
			heading?: string;
			target: TourSpotlighTargetName;
			content: React.JSX.Element | string;
			placement?: Placement;
			offset?: PopoverContentProps['offset'];
			actionLabel?: string;
			/** Called when the primary action button is clicked, in addition to completing the tour */
			onActionClick?: () => void;
	  }
	| {
			type: 'modal';
			heading?: string;
			content: React.JSX.Element | string;
			actionLabel?: string;
	  };

export const TourManager = ({
	children,
	tourItems,
	isTourStarted,
	endTour,
	onDismiss,
	onComplete,
	shouldDismissOnClickOutside = true,
	showBackdrop = true,
}: PropsWithChildren<{
	tourItems: TourItem[];
	isTourStarted: boolean;
	endTour: () => void;
	onDismiss?: () => void;
	onComplete?: () => void;
	shouldDismissOnClickOutside?: boolean;
	showBackdrop?: boolean;
}>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [tourItemIndex, setTourItemIndex] = useState<number>(0);
	const next = (): void => setTourItemIndex(tourItemIndex + 1);
	const back = (): void => setTourItemIndex(tourItemIndex - 1);
	const close = (): void => {
		setTourItemIndex(0);
		endTour();
	};
	// Called when clicking X button - triggers onDismiss callback
	const dismiss = (): void => {
		close();
		if (onDismiss) {
			onDismiss();
		}
	};
	// Called when clicking away or pressing Escape - just closes without triggering onDismiss
	const clickAway = (): void => {
		close();
	};
	const complete = (): void => {
		close();
		if (onComplete) {
			onComplete();
		}
	};

	const getCurrentSpotlightContent = (): React.JSX.Element | null => {
		if (!isTourStarted || tourItemIndex >= tourItems.length) {
			return null;
		}

		const currentItem = tourItems[tourItemIndex];

		if (currentItem.type !== 'spotlight') {
			return null;
		}

		const isLastStep = tourItemIndex === tourItems.length - 1;
		const hasPreviousStep = tourItemIndex > 0;

		return (
			<PopoverContent
				key={currentItem.target}
				dismiss={clickAway}
				isVisible
				offset={currentItem.offset}
				placement={currentItem.placement ?? 'right-end'}
				shouldDismissOnClickOutside={shouldDismissOnClickOutside}
				{...(isLastStep ? { done: complete } : { next: next })}
				{...(hasPreviousStep ? { back: back } : {})}
			>
				<SpotlightCard>
					<SpotlightHeader>
						{currentItem.heading && <SpotlightHeadline>{currentItem.heading}</SpotlightHeadline>}
						<SpotlightControls>
							<SpotlightDismissControl onClick={dismiss} />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightBody>
						{typeof currentItem.content === 'string' ? (
							<Box xcss={modalTextStyles}>{currentItem.content}</Box>
						) : (
							currentItem.content
						)}
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightActions>
							{hasPreviousStep && (
								<SpotlightSecondaryAction onClick={back}>
									{formatMessage(messages.goBack)}
								</SpotlightSecondaryAction>
							)}
							<SpotlightPrimaryAction
								onClick={
									isLastStep
										? () => {
												currentItem.onActionClick?.();
												complete();
											}
										: next
								}
							>
								{currentItem.actionLabel ??
									formatMessage(isLastStep ? messages.done : messages.next)}
							</SpotlightPrimaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		);
	};

	const getCurrentSpotlightTarget = (): TourSpotlighTargetName | null => {
		if (!isTourStarted || tourItemIndex >= tourItems.length) {
			return null;
		}

		const currentItem = tourItems[tourItemIndex];

		if (currentItem.type !== 'spotlight') {
			return null;
		}

		return currentItem.target;
	};

	const getCurrentModal = (): React.JSX.Element | null => {
		if (!isTourStarted || tourItemIndex >= tourItems.length) {
			return null;
		}

		const currentItem = tourItems[tourItemIndex];

		if (currentItem.type !== 'modal') {
			return null;
		}

		return (
			<Modal>
				{/* eslint-disable-next-line @atlaskit/design-system/use-modal-title */}
				<ModalHeader>
					<Box xcss={ModalHeaderStyles}>
						{currentItem.heading && <span>{currentItem.heading}</span>}
						<IconButton
							appearance="subtle"
							icon={CrossIcon}
							label={formatMessage(messages.dismiss)}
							onClick={dismiss}
						/>
					</Box>
				</ModalHeader>
				<ModalBody>
					<Box xcss={modalTextStyles}>{currentItem.content}</Box>
				</ModalBody>
				<ModalFooter>
					<Box xcss={modalTextStyles}>
						{tourItemIndex > 0 && (
							<Button appearance="subtle" onClick={back}>
								{formatMessage(messages.goBack)}
							</Button>
						)}
						{tourItemIndex < tourItems.length - 1 ? (
							<Button appearance="primary" onClick={next}>
								{formatMessage(messages.next)}
							</Button>
						) : (
							<Button appearance="primary" onClick={complete}>
								{currentItem.actionLabel ?? formatMessage(messages.done)}
							</Button>
						)}
					</Box>
				</ModalFooter>
			</Modal>
		);
	};

	const activeTarget = getCurrentSpotlightTarget();
	const spotlightContent = getCurrentSpotlightContent();
	const isBackdropVisible = showBackdrop && isTourStarted && activeTarget !== null;

	return (
		<>
			{isBackdropVisible && <Box xcss={backdropStyles} />}
			<TourSpotlightProvider activeTarget={activeTarget} content={spotlightContent}>
				{children}
			</TourSpotlightProvider>
			<ModalTransition>{getCurrentModal()}</ModalTransition>
		</>
	);
};
